import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { Validation } from '../../interfaces/validation/validation';
import { ValidationProblem } from '../../interfaces/validation/validation-problem';
import { ValidationLabels } from '../../interfaces/validation/validation-labels';
import { Order } from '../../interfaces/orders/order';
import { PathConversionService } from '../../services/path-conversion/path-conversion.service';
import { OrderManagerService } from '../../services/order-manager/order-manager.service';
import { ValidationService } from '../../services/validation/validation.service';
import { Observable, Subscription } from 'rxjs';
import { SlideDownAnimation } from '../animations';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'lib-validation-problems',
    templateUrl: './validation-problems.component.html',
    styleUrls: ['./validation-problems.component.css'],
    animations: [
        SlideDownAnimation
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ValidationProblemsComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @Input() saving$: Observable<boolean> | undefined;
  @Input() validation: Validation | null | undefined;
  @Input() useStaticValidation = false;
  @Input() collapsible = false;
  @Input() order!: Order;
  @Input() neverShowErrors = false;
  @Input() neverShowWarnings = false;
  @Input() neverShowNotices = false;
  @Input() lang: string = 'en';
  private _labels?: Partial<ValidationLabels>;
  @Input() set labels(value: Partial<ValidationLabels> | null | undefined) { this._labels = value ?? undefined; }
  /** Caller-provided labels override the translated defaults. */
  get labels(): ValidationLabels {
    const defaults: ValidationLabels = {
      notices: this.transloco.translate('common.validation.notices'),
      warnings: this.transloco.translate('common.validation.warnings'),
      errors: this.transloco.translate('common.validation.errors'),
    };
    return { ...defaults, ...this._labels } as ValidationLabels;
  }

  protected showWarnings = false;
  protected showErrors = false;
  protected showNotices = false;

  protected collapseWarnings = true;
  protected collapseErrors = true;
  protected collapseNotices = true;

  private savingSubscription?: Subscription;
  private validationSubscription?: Subscription;
  private isSaving = false;

  constructor(
    private orderManagerService: OrderManagerService,
    private pathConverstionService: PathConversionService,
    private validationService: ValidationService) { }

  ngOnInit(): void {
    if (!this.order) {
      this.order = this.orderManagerService.currentOrder.value;
    }

    if (this.saving$) {
      this.savingSubscription = this.saving$
        .subscribe(value => {
          this.isSaving = value;

          if (value === false) {
            this.showValidation();
          }
        });
    }

    if (this.useStaticValidation) {
      this.showValidation();
    } else {
      // Track the current validation rather than taking a one-time snapshot of it.
      this.validationSubscription = this.validationService.currentValidation$
        .subscribe(() => {
          if (!this.isSaving) {
            this.showValidation();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.savingSubscription?.unsubscribe();
    this.validationSubscription?.unsubscribe();
  }

  getMessage(problem: ValidationProblem): string {
    const convertedPath = this.pathConverstionService.convertPath(this.order, problem.path, false, problem.entityID);

    let message = problem.userFriendlyMessage ?? '';
    if (this.lang === 'fr') {
      message = problem.userFriendlyMessageFr || message;
    }

    if (convertedPath) {
      return `${convertedPath}: ${message}`;
    } else {
      return message;
    }
  }

  hasWarnings(): boolean {
    return (this.validation?.warnings?.filter(warn => warn.showForClient).length ?? 0) > 0;
  }

  hasErrors(): boolean {
    return (this.validation?.errors?.filter(err => err.showForClient).length ?? 0) > 0;
  }

  hasNotices(): boolean {
    return (this.validation?.notices?.length ?? 0) > 0;
  }

  showValidation() {
    if (!this.useStaticValidation) {
      this.validation = this.validationService.currentValidation$.value;
    }

    if (!this.order) {
      this.order = this.orderManagerService.currentOrder.value;
    }

    // Assigned rather than latched on, so results that no longer apply are taken back down
    this.showErrors = this.hasErrors() && !this.neverShowErrors;
    this.showWarnings = this.hasWarnings() && !this.neverShowWarnings;
    this.showNotices = this.hasNotices() && !this.neverShowNotices;
  }

  clearValidation() {
    this.validation = null;
    this.showWarnings = false;
    this.showErrors = false;
    this.showNotices = false;
  }

  shouldShowNotices() {
    if (this.collapsible) {
      return !this.collapseNotices;
    } else {
      return this.showNotices;
    }
  }

  getNotices(): ValidationProblem[] {
    return this.validation?.notices ?? [];
  }

  shouldShowWarnings() {
    if (this.collapsible) {
      return !this.collapseWarnings;
    } else {
      return this.showWarnings;
    }
  }

  getWarnings(): ValidationProblem[] {
    return this.validation?.warnings?.filter(w => w.showForClient) ?? [];
  }

  shouldShowErrors() {
    if (this.collapsible) {
      return !this.collapseErrors;
    } else {
      return this.showErrors;
    }
  }

  getErrors(): ValidationProblem[] {
    return this.validation?.errors?.filter(w => w.showForClient) ?? [];
  }
}
